import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Gavel, Clock, Users, AlertCircle, ChevronLeft, TrendingUp } from "lucide-react";

function timeRemaining(endsAt) {
  const ms = new Date(endsAt) - new Date();
  if (ms <= 0) return { label: "Terminée", critical: true };
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const critical = ms < 5 * 60000;
  if (d > 0) return { label: `${d}j ${h}h ${m}min`, critical };
  if (h > 0) return { label: `${h}h ${m}min`, critical };
  return { label: `${m}min ${s}s`, critical };
}

export default function AuctionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get(`/auctions/${id}`);
      setA(data);
      if (!bidAmount && data.status === "active") {
        const min = Math.max(data.current_price + 1, data.starting_price);
        setBidAmount(min.toFixed(0));
      }
    } catch { toast.error("Enchère introuvable"); navigate("/encheres"); }
  };

  useEffect(() => { load(); const id1 = setInterval(load, 15000); return () => clearInterval(id1); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { const id2 = setInterval(() => setTick(t => t+1), 1000); return () => clearInterval(id2); }, []);

  if (!a) return <div className="p-20 text-center">Chargement…</div>;

  const isSeller = user && user.user_id === a.seller_id;
  const minBid = Math.max(a.current_price + 1, a.starting_price);
  const tr = timeRemaining(a.ends_at);
  const isActive = a.status === "active";
  const isWinner = user && a.winner_id === user.user_id;

  const placeBid = async () => {
    if (!user) { toast.error("Connecte-toi pour enchérir"); navigate("/login"); return; }
    const amt = parseFloat(bidAmount);
    if (isNaN(amt) || amt < minBid) { toast.error(`Offre minimum : ${minBid.toFixed(2)}€`); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/auctions/${id}/bid`, { amount: amt });
      toast.success(data.extended ? "Mise placée ! +2 min anti-sniping ⏱" : "Mise placée 🎯");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSubmitting(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Annuler cette enchère ?")) return;
    try { await api.post(`/auctions/${id}/cancel`); toast.success("Annulée"); navigate("/dashboard"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const STATUS_LABEL = {
    active: "En cours",
    ended_pending_payment: "Terminée — gagnant en attente de paiement",
    ended_paid: "Terminée & payée",
    ended_no_winner: "Terminée sans acheteur",
    cancelled: "Annulée",
  };

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="auction-detail">
      <Link to="/encheres" className="label font-bold inline-flex items-center gap-1 mb-4"><ChevronLeft size={14}/> Toutes les enchères</Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Images */}
        <div>
          <div className="aspect-square overflow-hidden border-2 border-black bg-[color:var(--color-surface)] mb-3">
            {a.product?.images?.[0] && <img src={fileUrl(a.product.images[0])} className="w-full h-full object-cover" alt={a.product?.title}/>}
          </div>
          <p className="label">{a.product?.brand || "—"}</p>
          <h1 className="text-3xl font-black tracking-tighter mt-1">{a.product?.title}</h1>
          {a.product?.size && <p className="text-sm mt-2">Taille {a.product.size}</p>}
        </div>

        {/* Bid panel */}
        <div>
          {/* Status banner */}
          <div className={`p-3 mb-4 border-2 border-black ${isActive ? 'bg-[color:var(--color-mint)]' : a.status === 'ended_no_winner' ? 'bg-[color:var(--color-text-muted)] text-white' : 'bg-[color:var(--color-yellow)]'}`}>
            <p className="font-bold flex items-center gap-2">
              <Gavel size={16} strokeWidth={2.5}/> {STATUS_LABEL[a.status] || a.status}
            </p>
          </div>

          {/* Counter */}
          <div className="border-2 border-black p-5 mb-4 bg-white">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="label mb-1">Offre actuelle</p>
                <p className="display-num text-4xl font-black" style={{color:"var(--color-coral)"}}>{a.current_price?.toFixed(0)}€</p>
              </div>
              <div className="text-right">
                <p className="label mb-1">Temps restant</p>
                <p className={`display-num text-2xl font-black ${tr.critical && isActive ? 'text-[color:var(--color-coral)] animate-pulse' : ''}`} data-testid="time-remaining">
                  <Clock size={18} strokeWidth={2.5} className="inline -mt-0.5 mr-1"/>{tr.label}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm pt-3 border-t border-black">
              <span className="flex items-center gap-1"><Users size={14}/> {a.bid_count} enchère{a.bid_count > 1 ? "s" : ""}</span>
              <span className="text-[color:var(--color-text-muted)]">Prix de départ : {a.starting_price?.toFixed(0)}€</span>
            </div>
          </div>

          {/* Bid form */}
          {isActive && !isSeller && (
            <div className="border-2 border-black p-5 bg-[color:var(--color-yellow)] mb-4">
              <p className="label font-bold mb-2">Votre offre (min. {minBid.toFixed(0)}€)</p>
              <div className="flex gap-2">
                <input type="number" step="1" min={minBid} className="field flex-1" value={bidAmount} onChange={e=>setBidAmount(e.target.value)} data-testid="bid-amount-input"/>
                <button onClick={placeBid} disabled={submitting} className="btn btn-primary" data-testid="place-bid-btn">
                  <TrendingUp size={16} strokeWidth={2.5}/> Miser
                </button>
              </div>
              <p className="text-xs mt-2 text-black/70">Chaque mise dans les 2 dernières minutes prolonge l'enchère de 2 min. Anti-sniping activé.</p>
            </div>
          )}

          {/* Winner state */}
          {a.status === "ended_pending_payment" && isWinner && (
            <div className="border-2 border-black p-5 bg-[color:var(--color-mint)] mb-4">
              <p className="font-black text-lg mb-2">🎉 Vous avez gagné !</p>
              <p className="text-sm mb-3">Vous avez jusqu'au {new Date(a.winner_deadline).toLocaleString("fr-FR")} pour payer {a.top_bid_amount?.toFixed(2)}€.</p>
              <button onClick={()=>navigate(`/produit/${a.product_id}`)} className="btn btn-primary w-full" data-testid="pay-winner-btn">Aller payer →</button>
            </div>
          )}

          {/* Seller actions */}
          {isSeller && (
            <div className="border-2 border-black p-4 bg-white mb-4">
              <p className="label font-bold mb-2">Mode vendeur</p>
              {a.reserve_price && <p className="text-sm mb-2">Votre prix de réserve : <strong>{a.reserve_price.toFixed(2)}€</strong> {a.current_price >= a.reserve_price ? "✓ atteint" : "✗ non atteint"}</p>}
              {isActive && a.bid_count === 0 && (
                <button onClick={cancel} className="btn btn-ghost btn-sm" data-testid="cancel-auction-btn">Annuler l'enchère</button>
              )}
            </div>
          )}

          {/* Bid history */}
          <div className="border-2 border-black bg-white">
            <div className="p-3 hairline-b font-bold">Historique ({a.bids?.length || 0})</div>
            <div className="max-h-72 overflow-y-auto">
              {(!a.bids || a.bids.length === 0) && <p className="p-4 text-sm text-center text-[color:var(--color-text-muted)]">Pas encore d'offre</p>}
              {(a.bids || []).map((b, i) => (
                <div key={b.bid_id} className={`p-3 flex justify-between text-sm ${i < a.bids.length - 1 ? 'hairline-b' : ''}`}>
                  <span className="font-bold">{b.bidder_name || "Anonyme"}</span>
                  <span className="display-num font-bold">{b.amount?.toFixed(0)}€</span>
                  <span className="text-xs text-[color:var(--color-text-muted)]">{new Date(b.created_at).toLocaleTimeString("fr-FR",{hour:"2-digit", minute:"2-digit"})}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
